import * as React from "react";
import styled, { css } from "styled-components";
import { select } from "@hubper/theme";

type BaseProps = {
  isDragging?: boolean;
  disabled?: boolean;
  clickable: boolean;
};

const Base = styled.div<BaseProps>`
  display: flex;
  position: relative;
  box-shadow: ${p =>
    p.isDragging
      ? "0 2px 12px 0 rgba(0, 0, 0, 0.1)"
      : "0 2px 6px 0 rgba(0, 0, 0, 0.1)"};
  border-radius: 5px;
  overflow: hidden;
  height: 165px;
  background-color: white;
  transition: box-shadow ${select.speed("normal")};

  ${p =>
    !p.disabled &&
    p.clickable &&
    css`
      cursor: pointer;

      &:hover {
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
      }
    `};
`;

const Left = styled.div`
  position: relative;
  width: 40%;
  min-width: 150px;
  flex: 0 0 auto;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  background-color: ${select.color(["grey", 1])};
  object-fit: cover;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 auto;
`;

const MainTop = styled.div`
  display: flex;
  flex-direction: column;
  flex-direction: flex-start;
  flex: 1;
  padding: ${select.space(3)};
  padding-bottom: ${select.space(2)};
`;

const MainBottom = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MainBottomLeft = styled.div`
  display: flex;
`;

const MainBottomRight = styled.div`
  display: flex;
  margin-left: auto;
`;

const Overlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  background-color: rgba(255, 255, 255, 0.75);
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  imageSrc: string;
  bottomLeft?: React.ReactNode;
  bottomRight?: React.ReactNode;
  imageOverlay?: React.ReactNode;
  isDragging?: boolean;
  children?: any;
  overlayVisible?: boolean;
  overlay?: React.ReactNode;
  disabled?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

function ListTile(
  {
    style,
    className,
    imageSrc,
    imageOverlay,
    bottomLeft,
    bottomRight,
    isDragging,
    children,
    overlay,
    overlayVisible,
    disabled,
    onClick,
    ...rest
  }: Props,
  ref: any
) {
  return (
    <Base
      ref={ref}
      style={style}
      className={className}
      isDragging={isDragging}
      onClick={onClick}
      disabled={disabled}
      clickable={Boolean(onClick)}
      {...rest}
    >
      {overlay && overlayVisible && <Overlay>{overlay}</Overlay>}
      <Left>
        <Image src={imageSrc} />
        {imageOverlay}
      </Left>
      <Main>
        <MainTop>{children}</MainTop>
        {(bottomLeft || bottomRight) && (
          <MainBottom>
            {bottomLeft && <MainBottomLeft>{bottomLeft}</MainBottomLeft>}
            {bottomRight && <MainBottomRight>{bottomRight}</MainBottomRight>}
          </MainBottom>
        )}
      </Main>
    </Base>
  );
}

export default React.forwardRef(ListTile);
